import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import axios from 'axios';

const UserPointsLedgerModal = ({ show, onHide, userId, userName }) => {
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  const fetchPage = useCallback(
    async (pageNum, append) => {
      if (!userId) return;
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('accessToken');
        const { data } = await axios.get(
          `${API_BASE_URL}/admin/campus/users/${userId}/points-ledger`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { page: pageNum, limit: 50 },
          },
        );
        setUser(data.user);
        setPagination(data.pagination);
        setTransactions((prev) => (append ? [...prev, ...(data.transactions || [])] : data.transactions || []));
      } catch (err) {
        console.error('Ledger fetch error:', err);
        setError('Could not load transaction history.');
        if (!append) {
          setUser(null);
          setTransactions([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [API_BASE_URL, userId],
  );

  useEffect(() => {
    if (show && userId) {
      fetchPage(1, false);
    } else if (!show) {
      setUser(null);
      setTransactions([]);
      setPagination(null);
      setError(null);
    }
  }, [show, userId, fetchPage]);

  const loadMore = () => {
    if (!pagination) return;
    const next = pagination.page + 1;
    if (next > pagination.totalPages) return;
    fetchPage(next, true);
  };

  const formatPoints = (n) => {
    const v = Math.round(Number(n) || 0);
    return `${v.toLocaleString('en-IN')} pts`;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  const hasMore = pagination && pagination.page < pagination.totalPages;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      className="user-points-ledger-modal-root"
      dialogClassName="referral-complete-modal-wide user-points-ledger-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="h5 mb-0">Points ledger &amp; history</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        {(user || userName) && (
          <div className="mb-3 border rounded p-3 bg-light">
            <div className="fw-semibold text-dark mb-2">{user?.fullName || userName || 'User'}</div>
            {user && (
              <div className="text-muted">
                <div>
                  <span className="fw-semibold text-dark">Email:</span> {user.email || '—'}
                </div>
                <div className="mt-1">
                  <span className="fw-semibold text-dark">Wallet balance:</span>{' '}
                  {formatPoints(user.walletBalance)}
                </div>
              </div>
            )}
            {!user && loading && (
              <div className="text-muted small">Loading account details…</div>
            )}
          </div>
        )}
        {loading && transactions.length === 0 ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <div className="ledger-table-container">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="text-muted">Date</th>
                  <th className="text-muted">Type</th>
                  <th className="text-muted">Details</th>
                  <th className="text-muted text-end">Amount</th>
                  <th className="text-muted">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td className="text-muted">{formatDate(tx.createdAt)}</td>
                    <td className="text-capitalize">{tx.type}</td>
                    <td>
                      <div className="fw-medium">{tx.title}</div>
                      {tx.subtitle && <div className="text-muted small">{tx.subtitle}</div>}
                    </td>
                    <td
                      className="text-end fw-semibold"
                      style={{ color: tx.direction === 'credit' ? '#15803d' : '#b45309' }}
                    >
                      {tx.direction === 'credit' ? '+' : '−'}
                      {formatPoints(tx.amount)}
                    </td>
                    <td className="text-capitalize">{tx.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {hasMore && (
          <div className="text-center mt-3">
            <Button variant="outline-secondary" onClick={loadMore} disabled={loading}>
              {loading ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
        {!loading && transactions.length === 0 && !error && (
          <p className="text-muted text-center mb-0">No transactions yet.</p>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default UserPointsLedgerModal;
